import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { KeywordItem } from "../types/PerformanceTypes";
 import { fmtValue } from "../utils/PerformanceUtils";
import Card from "../ui/Card";
import SectionTitle from "../ui/SectionTitle";

// ── Delta badge ──────────────────────────────────────────────────────────────

const DeltaBadge = ({ current, previous }: { current: number; previous: number }) => {
  if (!previous) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  const up  = pct > 0;
  const eq  = pct === 0;
  if (eq) return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-400">
      <Minus size={9} /> 0%
    </span>
  );
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold
      ${up ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
      {up ? <TrendingUp size={9} strokeWidth={2.5} /> : <TrendingDown size={9} strokeWidth={2.5} />}
      {up ? "+" : ""}{pct}%
    </span>
  );
};

// ── Props ────────────────────────────────────────────────────────────────────

interface KeywordsTabProps {
  data:                KeywordItem[];
  compareEnabled?:     boolean;
  compareData?:        KeywordItem[];
  primaryPeriodLabel?:  string;
  comparePeriodLabel?:  string;
}

// ── Main ─────────────────────────────────────────────────────────────────────

const KeywordsTab = ({
  data,
  compareEnabled,
  compareData,
  primaryPeriodLabel  = "Période A",
  comparePeriodLabel  = "Période B",
}: KeywordsTabProps) => {
  const top = [...data].sort((a, b) => b.impressions - a.impressions).slice(0, 20);
  const max = top[0]?.impressions || 1;

  // Build a map of keyword → previous impressions for O(1) lookup
  const prevMap = new Map<string, number>(
    (compareData || []).map(k => [k.keyword, k.impressions])
  );
  const prevMax = Math.max(...(compareData || []).map(k => k.impressions), 1);

  return (
    <Card className="animate-[gbpFadeIn_0.2s_ease]">
      <div className="flex items-center justify-between mb-1">
        <SectionTitle>Top mots-clés de recherche</SectionTitle>
        {compareEnabled && (
          <div className="flex items-center gap-3 text-[11px]">
            <span className="flex items-center gap-1.5 text-blue-600 font-semibold">
              <span className="h-2 w-2 rounded-full bg-blue-500 inline-block" />
              {primaryPeriodLabel}
            </span>
            <span className="flex items-center gap-1.5 text-violet-400 font-semibold">
              <span className="h-2 w-2 rounded-full bg-violet-400 inline-block" />
              {comparePeriodLabel}
            </span>
          </div>
        )}
      </div>

      {top.length === 0 ? (
        <p className="text-slate-400 text-[13px]">Aucune donnée disponible.</p>
      ) : (
        <div className="flex flex-col">
          {top.map((kw, i) => {
            const prevVal = prevMap.get(kw.keyword);
            const barColor = `hsl(${224 + i * 6}, 68%, ${52 + i}%)`;
            const prevBarPct = prevVal !== undefined ? (prevVal / Math.max(max, prevMax)) * 100 : 0;

            return (
              <div
                key={kw.keyword}
                className={`flex items-center gap-3 py-2.5 group transition-colors duration-150
                  ${i < top.length - 1 ? "border-b border-slate-50" : ""}`}
              >
                {/* Rank badge */}
                <span className={`w-6 h-6 rounded-md flex items-center justify-center
                  text-[11px] font-bold flex-shrink-0
                  ${i < 3 ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-400"}`}>
                  {i + 1}
                </span>

                {/* Keyword */}
                <span className={`flex-1 text-[13px] text-slate-800 min-w-0
                  overflow-hidden text-ellipsis whitespace-nowrap
                  ${i < 3 ? "font-semibold" : "font-normal"}`}>
                  {kw.keyword}
                </span>

                {/* Stacked progress bar */}
                <div className="relative w-36 flex-shrink-0">
                  {/* Compare bar (behind) */}
                  {compareEnabled && prevVal !== undefined && (
                    <div className="absolute inset-y-0 left-0 h-[5px] top-[3px] rounded-full opacity-30 transition-[width] duration-500 ease-out"
                         style={{ width: `${prevBarPct}%`, background: barColor }} />
                  )}
                  {/* Primary bar */}
                  <div className="h-[5px] bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-[width] duration-500 ease-out group-hover:opacity-85"
                      style={{ width: `${(kw.impressions / max) * 100}%`, background: barColor }}
                    />
                  </div>
                  {/* Compare bar track */}
                  {compareEnabled && prevVal !== undefined && (
                    <div className="h-[3px] bg-slate-50 rounded-full overflow-hidden mt-[3px]">
                      <div
                        className="h-full rounded-full opacity-40 transition-[width] duration-500 ease-out"
                        style={{ width: `${prevBarPct}%`, background: barColor }}
                      />
                    </div>
                  )}
                </div>

                {/* Value + delta */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="w-11 text-xs font-semibold text-slate-800 text-right font-mono">
                    {fmtValue(kw.impressions)}
                  </span>
                  {compareEnabled && prevVal !== undefined && (
                    <DeltaBadge current={kw.impressions} previous={prevVal} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default KeywordsTab;